import React, { useContext } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { Rutas } from '@/constants/rutas';
import { Context } from '@/context';
import { getTranslatedTeamName } from '@/data/equipos';
import CustomLink from './custom/custom-link';
import PredictingManagerIcon from './icons/predicting-manager-icon';
import TelegramIcon from './icons/telegram-icon';
import TelegramIcons from './icons/telegram-icons';
import Tiktok from './icons/tiktok';
import TwitterIcon from './icons/twitter-icon';
import WhatsappIcon from './icons/whatsapp-icon';
import { NavigationCorporativoItems } from './navigation/sections/NavigationCorporativoItems';
import AFLogo, { LOGO_VARIANTS } from '@/DesignSystem/components/logo/AFLogo';
import CustomTeamImageWithDefault from './custom/CustomTeamImageWithDefault';
import CustomTooltip from '@/DesignSystem/components/tooltip/CustomTooltip';
import Link from 'next/link';

function FooterSection() {
    const currentYear = new Date().getFullYear();
    const { state } = useContext(Context);
    const { partidos } = state;

    const theme = useTheme();

    const getPartidosColumn = () => {
        if (!partidos || partidos.length === 0) {
            return null;
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {partidos.map((partido, index) => (
                    <CustomTooltip
                        label={`${partido.home?.name} vs ${partido.away?.name}: Onces posibles`}
                        key={index}
                    >
                        <CustomLink
                            key={index}
                            href={`${Rutas.PARTIDO}/${partido.id}/alineaciones-probables`}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '4px',
                                    color: theme.palette.footer.text,
                                    padding: '8px',
                                    minWidth: '120px',
                                    borderRadius: '4px',
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '14px', color: theme.palette.footer.text }}>
                                        {getTranslatedTeamName(partido?.home?.id, partido?.home?.name)}
                                    </Typography>
                                    <CustomTeamImageWithDefault
                                        teamId={partido.home?.id}
                                        alt={`Alineación probable de ${partido.home?.name}`}
                                        width={24}
                                        height={24}
                                    />
                                </Box>

                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '14px', color: 'primary.main', fontWeight: 500 }}
                                >
                                    vs
                                </Typography>

                                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <CustomTeamImageWithDefault
                                        teamId={partido.away?.id}
                                        alt={`Once posible de ${partido.away?.name}`}
                                        width={24}
                                        height={24}
                                    />
                                    <Typography sx={{ fontSize: '14px', color: theme.palette.footer.text }}>
                                        {getTranslatedTeamName(partido?.away?.id, partido?.away?.name)}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '10px',
                                        opacity: 0.8,
                                        position: 'relative',
                                        bottom: 0,
                                        right: 0,
                                        color: theme.palette.footer.text,
                                    }}
                                >
                                    {partido.top} {partido.bottom}
                                </Typography>
                            </Box>
                        </CustomLink>
                    </CustomTooltip>
                ))}
            </Box>
        );
    };

    const allNavigationItems = NavigationCorporativoItems;

    return (
        <Box
            component={'footer'}
            className="footer-container"
            bgcolor={theme.palette.footer.background}
            sx={{
                borderTop: `1px solid ${theme.palette.border.main}`,
            }}
        >
            <Box className="container" sx={{ py: 3 }}>
                {/* Logo */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <AFLogo width={200} variant={LOGO_VARIANTS.TEXTO_Y_LETRAS_VERDES} />
                </Box>

                {/* Información - Ocupa toda la pantalla */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        sx={{
                            color: theme.palette.footer.text,
                            fontSize: '20px',
                            mb: 2,
                            textAlign: 'center',
                        }}
                        fontWeight={500}
                    >
                        Información
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.palette.footer.text,
                            lineHeight: '26px',
                            width: '100%',
                            textAlign: 'center',
                        }}
                    >
                        Analítica Fantasy: Tu web de Fútbol Fantasy, con noticias, alineaciones probables,
                        estadísticas, mercado fantasy, información de equipos y jugadores. La Liga Fantasy
                        Dazn. Nos especializamos en La Liga de España y cubrimos todos los juegos de fantasy
                        fútbol como son La Liga Fantasy, Biwenger, Fantasy Marca, Mister Fantasy, Comunio,
                        etc.
                    </Typography>
                </Box>

                {/* Alineaciones probables */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        sx={{
                            color: theme.palette.footer.text,
                            fontSize: '20px',
                            mb: 2,
                            textAlign: 'center',
                        }}
                        fontWeight={500}
                    >
                        Alineaciones probables
                    </Typography>
                    {getPartidosColumn()}
                </Box>

                {/* Conoce Analítica Fantasy */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        sx={{
                            color: theme.palette.footer.text,
                            fontSize: '20px',
                            mb: 2,
                            textAlign: 'center',
                        }}
                        fontWeight={500}
                    >
                        Conoce Analítica Fantasy
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                        {allNavigationItems.map((link, index) => (
                            <Link key={index} href={link.path} className="link">
                                {link.title}
                            </Link>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Predicting Manager */}
            <CustomLink
                href={Rutas.PREDICTING_MANAGER}
                linkStyles={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    marginTop: '8px',
                }}
            >
                <PredictingManagerIcon iconWidth={50} iconHeight={50} size={150} />
                <Typography sx={{ color: theme.palette.footer.text }}>Juega al Predicting Manager</Typography>
            </CustomLink>

            {/* Redes sociales */}
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={2}>
                <Typography sx={{ color: theme.palette.footer.text, whiteSpace: 'nowrap' }}>
                    Nuestras redes sociales:
                </Typography>
                <Box display="flex" alignItems="center" gap={1} py={1}>
                    <TwitterIcon
                        onClick={() => window.open('https://twitter.com/AnaliticaLaLiga', '_blank')}
                    />
                    <Tiktok
                        color={'#fff'}
                        width={18}
                        height={18}
                        onClick={() => window.open('https://www.tiktok.com/@analitica.fantasy', '_blank')}
                    />
                    <TelegramIcon onClick={() => window.open('https://t.me/+pOyVGp5tSEpkMzBh', '_blank')} />
                    <TelegramIcons onClick={() => window.open('https://t.me/+9Qf0bWVpF35jNWEx', '_blank')} />
                    <WhatsappIcon
                        color={'#fff'}
                        width={20}
                        height={20}
                        onClick={() =>
                            window.open('https://whatsapp.com/channel/0029VawDrasLtOjKouGAfp24', '_blank')
                        }
                    />
                </Box>
            </Box>

            {/* Buzón del ciudadano */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: 2,
                }}
            >
                <Typography sx={{ color: theme.palette.footer.text, fontSize: '14px' }}>
                    ¿Encuentras un fallo? Repórtalo en el{' '}
                    <Link href={Rutas.BUZON_CIUDADANO} prefetch={false} className="link">
                        Buzón del Ciudadano
                    </Link>{' '}
                    y te atenderemos cuanto antes
                </Typography>
            </Box>

            {/* Copyright */}
            <Typography sx={{ color: theme.palette.footer.text }} textAlign={'center'} py={1}>
                Analítica Fantasy © {currentYear}{' '}
            </Typography>
        </Box>
    );
}

export default FooterSection;
